/**
 * Судьбы: что стало с вилками, которые прошли перед глазами за эту смену.
 *
 * Вопрос, на который до этого модуля не отвечал никто — ни мы, ни конкуренты:
 * **успел бы я?** Лента показывает, что есть сейчас; через три секунды строка
 * исчезает, и человек остаётся с ощущением, а не с числом. Ощущение врёт в обе
 * стороны: после трёх подряд исчезнувших вилок кажется, что не успеть никогда,
 * а после одной пойманной — что успеваешь всегда.
 *
 * Считается по тому, что человек видел САМ, а не по нашей истории. Серверная
 * медиана (`sv`) отвечает «такие живут N секунд» — это про рынок. Здесь другое
 * утверждение: вот сколько вилок умерло у вас на глазах, и вот сколько из них
 * пережили ВАШ темп. Второе число зависит от того, как быстро человек ходит по
 * конторам, и поэтому не может прийти с сервера.
 *
 * ## Почему замер честнее, чем кажется, и где у него граница
 *
 * Вилка, которая уже лежала в ленте, когда вкладку открыли, прожила НЕ МЕНЬШЕ
 * измеренного: начало её жизни мы не видели. Такие судьбы помечены `sure:
 * false` и в медиану не идут — иначе первая минута после открытия вкладки
 * занижала бы живучесть всей смены. Считать по ним «успел бы» всё же можно:
 * нижняя граница, превысившая темп, превысила его наверняка.
 *
 * Смерть по нашей вине от смерти по рынку отделена кодом причины (`why` из
 * каталога `/v1/meta`): «ушла из ленты источника» и «мы её скрыли правилом» —
 * разные новости, и складывать их в одно число значило бы прятать свой фильтр
 * за поведением рынка.
 *
 * Журнал живёт в памяти вкладки и умирает вместе с ней. Это осознанно: смена —
 * это смена, а не всё время с установки. Хранить его на сервере значило бы
 * завести состояние на каждого зрителя ради строки в подвале.
 */

import { create } from 'zustand';

/** Сколько судеб держим в памяти. Медиана по последним — про сейчас, а не про вчера. */
export const KEEP = 500;

/**
 * Запас на первый кадр: вилки из него «родились» в момент подключения, хотя на
 * самом деле жили до нас. Всё, что умерло, родившись в этом окне, считается
 * нижней границей.
 */
const GRACE_MS = 3000;

/** Когда открыли вкладку. Модуль грузится вместе с ней, отдельного события не нужно. */
const OPENED = Date.now();

export interface Fate {
  id: string;
  /** Сколько прожила на наших глазах, секунды. */
  lived: number;
  /** Измерено от рождения (`true`) или это нижняя граница (`false`). */
  sure: boolean;
  /** Код причины из каталога `deaths`. */
  why: string;
  /**
   * Процент вилки на момент смерти. Именно процент, а не рубли: рубли зависят от
   * банка и курса, а банк человек меняет посреди смены — журнал бы поехал. Во
   * что превратить процент, решает тот, кто показывает, и делает это на текущем
   * банке.
   */
  roiPct: number;
}

export interface FatesState {
  fates: Fate[];
  note: (fate: Fate) => void;
  clear: () => void;
}

export const useFates = create<FatesState>((set, get) => ({
  fates: [],
  note: (fate) => {
    const kept = [...get().fates, fate];
    set({ fates: kept.length > KEEP ? kept.slice(kept.length - KEEP) : kept });
  },
  clear: () => set({ fates: [] }),
}));

/**
 * Отметить смерть вилки. Зовётся из хранилища экрана — единственного места, где
 * смерть проходит ровно один раз.
 *
 * `bornAt` — когда мы увидели строку впервые. `undefined` означает, что мы её
 * не видели вовсе (снапшот после переподключения пришёл уже без неё), и такую
 * судьбу записывать нечем: выдуманная живучесть хуже отсутствующей.
 */
export function noteFate(
  id: string,
  why: string,
  bornAt: number | undefined,
  roiPct: number,
  now: number = Date.now(),
): void {
  if (bornAt === undefined) return;
  useFates.getState().note({
    id,
    lived: Math.max(0, Math.round((now - bornAt) / 1000)),
    sure: bornAt > OPENED + GRACE_MS,
    why,
    roiPct,
  });
}

export interface Verdict {
  /** Сколько вилок умерло у человека на глазах. */
  deaths: number;
  /** Из них прожили дольше его темпа — то есть он бы успел. */
  made: number;
  /** Медиана прожитого по точно измеренным, секунды. `null` — таких ещё нет. */
  median: number | null;
  /** Причины смерти: код → сколько раз. */
  why: Record<string, number>;
  /** Лучший процент среди тех, на которые он бы НЕ успел. `null` — таких не было. */
  missedBest: number | null;
}

/**
 * Итог смены по журналу судеб.
 *
 * `tempo === null` — темп ещё не измерен, и вопрос «успел бы» не задан: ответ
 * «успел бы на все» при неизвестном темпе был бы не оптимизмом, а ошибкой.
 */
export function verdict(fates: readonly Fate[], tempo: number | null): Verdict {
  const why: Record<string, number> = {};
  let made = 0;
  let missedBest: number | null = null;
  const measured: number[] = [];
  for (const fate of fates) {
    why[fate.why] = (why[fate.why] ?? 0) + 1;
    if (fate.sure) measured.push(fate.lived);
    if (tempo === null) continue;
    // Нижняя граница, превысившая темп, превысила его наверняка: начало жизни мы
    // не видели, а значит вилка жила ДОЛЬШЕ измеренного, а не меньше.
    if (fate.lived >= tempo) made += 1;
    else if (missedBest === null || fate.roiPct > missedBest) missedBest = fate.roiPct;
  }
  measured.sort((a, b) => a - b);
  const median =
    measured.length === 0
      ? null
      : measured.length % 2 === 1
        ? measured[(measured.length - 1) / 2]!
        : Math.round((measured[measured.length / 2 - 1]! + measured[measured.length / 2]!) / 2);
  return { deaths: fates.length, made, median, why, missedBest };
}
